import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// COMPONENTS
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';

// SERVICES
import { userApi } from '@/main/api';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { CompanyFilterRequest, UserFilterRequest } from '@/main/models/request';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import {
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isValueQueryParams,
  mountReducerWithData,
  removeEmptyValues
} from '@/main/services/utils';
import { userIsAdmin } from '@/main/services/utilsUser';
import { StatusUserEnum } from '@/main/services/enum/StatusUserEnum';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { KeyValue } from '@/main/models/common';
import { getOptionsStatusUser } from '@/main/services/data/data';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';

// REDUX
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getCompanyType,
  useCompanyTypeReducer
} from '@/main/store/companyType';
import {
  getCompany,
  resetReducerCompany,
  useCompanyReducer
} from '@/main/store/company';
import { useUserReducer } from '@/main/store/user';
import {
  getCompanyBranch,
  resetReducerCompanyBranch,
  useCompanyBranchReducer
} from '@/main/store/companyBranch';

import UserSearchGrid, {
  IGridData,
  INITIAL_GRID_STATE
} from './components/userSearchGrid';
import * as S from './styles';
import { StatusCompanyEnum } from '@/main/services/enum/StatusCompanyEnum';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import useFormQueryParams from '@/main/hooks/useformQueryParams';

const INITIAL_FORM_STATE = {
  country: null as KeyValue | null,
  companyType: null as KeyValue | null,
  name: '',
  login: '',
  company: null as KeyValue | null,
  companyBranch: null as KeyValue | null,
  status: null as KeyValue | null
};

type ISearchUserForm = typeof INITIAL_FORM_STATE;

function UserScreen() {
  // Reducers
  const userReducer = useUserReducer();
  const countryReducer = useCountryReducer();
  const companyTypeReducer = useCompanyTypeReducer();
  const companyReducer = useCompanyReducer();
  const companyBranchReducer = useCompanyBranchReducer();
  const optionsStatusUser = getOptionsStatusUser();

  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    customHandleSubmit,
    control,
    reset,
    queryParamsValues,
    formState: { errors },
    setValue,
    getValues,
    watch
  } = useFormQueryParams({
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      country: countryReducer.countriesUser,
      companyType: companyTypeReducer.companyType,
      company: companyReducer.company,
      companyBranch: companyBranchReducer.companyBranch,
      status: mountReducerWithData(optionsStatusUser)
    },
    onSubmit: onSubmit
  });
  const watcherCompanyType = watch('companyType');

  // States
  const [showFilters, setShowFilters] = useState(true);
  const [formFilters, setFormFilters] = useState<UserFilterRequest | null>(
    null
  );
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);

  // Methods & Variables for Form
  const typeCompanyUser = !userIsAdmin()
    ? String(userReducer.currentUser?.idCompanyType)
    : null;

  const userIsInsurer =
  userReducer.currentUser?.idCompanyType === CompanyTypeEnum.Insurer;

  const userIsAdminOrInsurer =
  userIsAdmin() ||
  userReducer.currentUser?.idCompanyType === CompanyTypeEnum.Insurer;

  const optionDefaultCountryDrop = getDefaultOptionDrop(
    countryReducer.countriesUser.data
  );
  const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(
    companyTypeReducer.companyType.data,
    typeCompanyUser
  );
  const optionDefaultCompanyDrop = getDefaultOptionDrop(
    companyReducer.company.data
  );
  const optionDefaultCompanyBranchDrop = getDefaultOptionDrop(
    companyBranchReducer.companyBranch.data
  );

 const isDisabledCompanyTypeDrop = () => 
    companyTypeReducer.companyType.isLoading || 
    !companyTypeReducer.companyType.data || 
    (!userIsAdminOrInsurer && optionDefaultCompanyTypeDrop !== null) || 
    (companyTypeReducer.companyType.data.length <= 1 
    && !isValueQueryParams(getValues().companyType));

 const isDisabledCompanyDrop = () =>
   getValues().companyType === null ||
  !companyReducer.company.data ||
  companyReducer.company.data.length === 1  ||
  companyReducer.company.data.length === 0 
  !isValueQueryParams(getValues().company);

const isDisabledCompanyBranchDrop = () =>
   getValues().companyType === null ||
  !companyBranchReducer.companyBranch.data ||
  companyBranchReducer.companyBranch.data.length === 1  ||
  companyBranchReducer.companyBranch.data.length === 0;

  // Handle Functions
  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  function handleNew() {
    dispatch(resetReducerCompany());
    navigate(RoutesConstants.registrationRegisterUser);
  }
  function handleClear() {
    reset(INITIAL_FORM_STATE);

    setGridData(INITIAL_GRID_STATE);
    setFormFilters(null);

    const statusActive = optionsStatusUser.find(
      item => item.key === StatusUserEnum.Active
    );
    setValue('status', statusActive);

    if (optionDefaultCountryDrop !== null) {
      setValue('country', optionDefaultCountryDrop);

      if (optionDefaultCompanyTypeDrop !== null) {
        setValue('companyType', optionDefaultCompanyTypeDrop);

        if (optionDefaultCompanyDrop !== null)
          setValue('company', optionDefaultCompanyDrop);
        if (optionDefaultCompanyBranchDrop !== null)
          setValue('companyBranch', optionDefaultCompanyBranchDrop);
      }
    } else {
      dispatch(resetReducerCompany());
      dispatch(resetReducerCompanyBranch());
    }
  }

  // Asynchronous functions
  function callCompanyApiDomain(
    countrySelected: KeyValue | null,
    companyTypeSelected: KeyValue | null
  ) {
    if (!isValueQueryParams(countrySelected) || !isValueQueryParams(companyTypeSelected)) {
      setValue('company', null);
      setValue('companyBranch', null);
    }

    dispatch(resetReducerCompany());
    dispatch(resetReducerCompanyBranch());

    if (countrySelected !== null && companyTypeSelected !== null) {
      const request: CompanyFilterRequest = {
        guidCountry: String(countrySelected.key),
        type: 
          Number(companyTypeSelected.key) === CompanyTypeEnum.BodyShop
            ? HeadQuarterBranchFilterEnum.Only_Headquarters
            : HeadQuarterBranchFilterEnum.All,
            seeOnlyCompaniesWithAnAgreement: userIsInsurer && Number(companyTypeSelected.key) !== CompanyTypeEnum.Insurer ? true : false,
            guidCompany: !userIsAdmin() ? userReducer.currentUser?.guidCompany : null,
            idStatusRegister: StatusCompanyEnum.Active
      };
      if (companyTypeSelected) {
        request.idCompanyType = String(companyTypeSelected.key);
      }
      dispatch(getCompany(request));
    }
  }
  function callCompanyBranchApiDomain(
    countrySelected: KeyValue | null,
    companySelected: KeyValue | null
  ) {
    // Clean fields & reducers
    if (!isValueQueryParams(countrySelected) || !isValueQueryParams(companySelected)) {
      setValue('companyBranch', null);
    }
    dispatch(resetReducerCompanyBranch());

    if (countrySelected !== null && companySelected !== null) {
      const request: CompanyFilterRequest = {
        guidCountry: String(countrySelected.key),
        type: HeadQuarterBranchFilterEnum.Only_Branches,
        idStatusRegister: StatusCompanyEnum.Active,
        seeOnlyCompaniesWithAnAgreement: userIsInsurer ? true : false,
        guidCompanyHeadQuarter: String(companySelected.key)
      };
      dispatch(getCompanyBranch(request));
    }
  }

  function onSubmit(data: ISearchUserForm) {
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const request = removeEmptyValues<UserFilterRequest>({
      guidCountry: String(data.country?.key),
      idCompanyType: Number(data.companyType?.key) as CompanyTypeEnum,
      guidCompany: data.company ? String(data.company.key) : null,
      guidCompanyBranch: data.companyBranch
        ? String(data.companyBranch.key)
        : null,
      idStatus: data.status ? Number(data.status.key) : null,
      name: data.name,
      login: data.login,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    });

    setFormFilters(request);

    userApi
      .getSearchGrid(request)
      .then(response => {
        setGridData({
          isLoading: false,
          totalItems: response.data.totalNumberOfRecords,
          data:
            response.data.totalNumberOfRecords > 0 ? response.data.results : []
        });
      })
      .catch(error => { });
    setShowFilters(!showFilters);
  }

  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getCompanyType({
      screenCompanytype: true
    }));

    return () => {
      dispatch(resetReducerCompany());
      dispatch(resetReducerCompanyBranch());
    };
  }, []);

  useEffect(() => {
  const hasCompanyTypeInQuery = isValueQueryParams(queryParamsValues.companyType);

  if (!hasCompanyTypeInQuery) {
    if (optionDefaultCountryDrop && optionDefaultCompanyTypeDrop) {
      callCompanyApiDomain(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
    }
  }
  }, [optionDefaultCountryDrop, optionDefaultCompanyTypeDrop]);

  useEffect(() => {
  const hasCompanyInQuery = isValueQueryParams(queryParamsValues.company);

  if (!hasCompanyInQuery) {
    if (optionDefaultCountryDrop && optionDefaultCompanyDrop) {
      callCompanyBranchApiDomain(optionDefaultCountryDrop, optionDefaultCompanyDrop);
    }
  }
}, [optionDefaultCountryDrop, optionDefaultCompanyDrop]);

  // useEffect to load queryParams
  useEffect(() => {
    const mountCountryQueryParams = isNullUndefined(queryParamsValues.country) ? null : queryParamsValues.country;
    const mountCompanyTypeQueryParams = isNullUndefined(queryParamsValues.companyType) ? null : queryParamsValues.companyType;
    const mountCompanyQueryParams = isNullUndefined(queryParamsValues.company) ? null : queryParamsValues.company;

    if (!isNullUndefined(mountCompanyTypeQueryParams)) callCompanyApiDomain(mountCountryQueryParams, mountCompanyTypeQueryParams);
    if (!isNullUndefined(mountCompanyQueryParams)) callCompanyBranchApiDomain(mountCountryQueryParams, mountCompanyQueryParams);

  }, [JSON.stringify(queryParamsValues)])

  // CONSTANTS LAYOUT
  const titleScreen = `${getTranslateByKey(
    'information.registration'
  )} > ${getTranslateByKey('information.searchUser')}`;

  const hasBodyshopCompanyType =
    watcherCompanyType?.key === String(CompanyTypeEnum.BodyShop);

  return (
    <ContainerScreen hasBackground title={titleScreen}>
      <S.Container>
        <AreaButton>
          <Button onClick={handleFilters} id="id-searchUser-button-filters">
            {getTranslateByKey('information.filters')}
          </Button>
          <Button onClick={handleNew} id="id-searchUser-button-newUser">
            {getTranslateByKey('information.newUser')}
          </Button>
        </AreaButton>
        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={customHandleSubmit} noValidate>
            <S.RowInputs>
              <KogAutoCompleteControlled
                nameCustom="country"
                control={control}
                label={getTranslateByKey('information.country')}
                options={countryReducer.countriesUser.data}
                isLoading={countryReducer.countriesUser.isLoading}
                rulesCustom={{ required: true }}
                errorMessageCustom={
                  errors.country?.type === 'required'
                    ? getTranslateByKey('information.countryRequired', true)
                    : ''
                }
                hasErrorCustom={!!errors.country}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  const companyTypeSelected = getValues().companyType;
                  if (companyTypeSelected) {
                    callCompanyApiDomain(optionSelected, companyTypeSelected);
                  }
                }}
                valueDefaultKey={optionDefaultCountryDrop?.key}
                setValueCustom={setValue}
                disabled={optionDefaultCountryDrop !== null}
                id="id-searchUser-select-country"
              />
              <KogInputTextControlledNew
                name="name"
                label={getTranslateByKey('information.name')}
                inputProps={{ maxLength: 100 }}
                control={control}
                id="id-searchUser-txt-name"
              />
              <KogInputTextControlledNew
                name="login"
                label={getTranslateByKey('information.login')}
                inputProps={{ maxLength: 50 }}
                control={control}
                id="id-searchUser-txt-login"
              />
              <KogAutoCompleteControlled
                nameCustom="companyType"
                control={control}
                label={getTranslateByKey('information.companyType')}
                rulesCustom={{ required: true }}
                hasErrorCustom={!!errors.companyType}
                errorMessageCustom={
                  errors.companyType?.type === 'required'
                    ? getTranslateByKey('information.companyTypeRequired', true)
                    : ''
                }
                options={companyTypeReducer.companyType.data}
                isLoading={companyTypeReducer.companyType.isLoading}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  callCompanyApiDomain(getValues().country, optionSelected);
                }}
                disabled={isDisabledCompanyTypeDrop()}
                valueDefaultKey={
                  isValueQueryParams(queryParamsValues.companyType)
                    ? queryParamsValues.companyType.key
                    : optionDefaultCompanyTypeDrop?.key
                }
                setValueCustom={setValue}
                id="id-searchUser-select-companyType"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.company')}
                isDefaultValueUnique
                control={control}
                nameCustom="company"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={companyReducer.company.data}
                isLoading={companyReducer.company.isLoading}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  if (hasBodyshopCompanyType) {
                    callCompanyBranchApiDomain(
                      getValues().country,
                      optionSelected
                    );
                  }
                }}
                disabled={isDisabledCompanyDrop()}
                valueDefaultKey={
                  isValueQueryParams(queryParamsValues.company)
                    ? queryParamsValues.company.key
                    : optionDefaultCompanyDrop?.key
                }
                setValueCustom={setValue}
                id="id-searchUser-select-company"
              />
              {hasBodyshopCompanyType && (
                <KogAutoCompleteControlled
                  label={getTranslateByKey('information.companyBranch')}
                  control={control}
                  isDefaultValueUnique
                  nameCustom="companyBranch"
                  rulesCustom={{ required: false }}
                  errorMessageCustom=""
                  hasErrorCustom={false}
                  options={companyBranchReducer.companyBranch.data}
                  isLoading={companyBranchReducer.companyBranch.isLoading}
                  onChangeCustom={() => { }}
                  valueDefaultKey={optionDefaultCompanyBranchDrop?.key}
                  setValueCustom={setValue}
                  disabled={isDisabledCompanyBranchDrop()}
                  id="id-searchUser-select-companyBranch"
                />
              )}
            </S.RowInputs>
            <S.RowInputs>
              <KogAutoCompleteControlled
                nameCustom="status"
                label={getTranslateByKey('information.status')}
                control={control}
                options={optionsStatusUser}
                isLoading={false}
                valueDefaultKey={StatusUserEnum.Active}
                setValueCustom={setValue}
                hasErrorCustom={!!errors.status}
                rulesCustom={{ required: true }}
                errorMessageCustom={
                  errors.status?.type === 'required'
                    ? getTranslateByKey('information.statusRequired', true)
                    : ''
                }
                onChangeCustom={() => { }}
                id="id-searchUser-select-status"
              />
            </S.RowInputs>
            <KOGAreaButtons>
              <SecondaryButton
                type="submit"
                id="id-searchUser-btn-search">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                onClick={handleClear}
                id="id-searchUser-btn-clear">
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>
        <UserSearchGrid
          formFilters={formFilters}
          gridData={gridData}
          setGridData={setGridData}
        />
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(UserScreen);
